import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Navbar from "../../components/layout/Navbar/Navbar";
import Sidebar from "../../components/layout/Sidebar/Sidebar";
import { getCargoIdSidebar } from "../../constants/sidebarConfig";
import apiService from "../../services/api";
import { usePopup } from "../../components/common/Popup";
import { required } from "../../utils/validation";
import { sortByField } from "../../utils/sortFilters";
import editIcon from "../../assets/edit.png";
import trashIcon from "../../assets/trash.png";
import addCursoButton from "../../assets/addCursoButton.png";

const NAVBAR_HEIGHT = 65;
const SIDEBAR_WIDTH = 300;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f9f9f9;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 60px;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH}px;
  overflow-y: auto;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
`;

const HeaderBar = styled.header`
  display: flex;
  align-items: center;
  background: #f9f9f9;
  padding: 12px 40px;
  border-bottom: 1px solid #ddd;
  font-size: 20px;
  font-weight: 500;
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AddAreaButton = styled.button`
  display: flex;
  align-items: center;
  background-color: #6ab8a8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 14px;
  margin-left: 10px;
  cursor: pointer;

  img {
    margin-left: 8px;
    width: 18px;
    height: 18px;
  }
`;

const DescriptionBar = styled.div`
  padding: 16px 40px;
  background: #f2f0f0;
  border-bottom: 1px solid #dfe3e8;
  color: #6b7280;
  font-size: 16px;
  font-weight: 500;
`;

const FormContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  padding: 24px;
  box-sizing: border-box;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  width: min(700px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #ffffff;
  border-radius: 11px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #e6e6e6;
  font-size: 18px;
  font-weight: 600;
`;

const ModalCloseButton = styled.button`
  padding: 0;
  background: none;
  border: none;
  color: #555;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px;
  margin-bottom: 6px;
  font-weight: ${(props) => (props.bold ? "bold" : "normal")};
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.hasError ? "#d00" : "#ccc")};
  border-radius: 9px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: ${(props) => (props.hasError ? "2px" : "6px")};

  &:focus {
    border-color: ${(props) => (props.hasError ? "#d00" : "#89c2a1")};
    outline: none;
  }
`;

const Button = styled.button`
  padding: 10px 24px;
  background-color: #6ab8a8;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 16px;
  margin: 0;
  align-self: flex-end;
  min-width: 120px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);

  &:hover {
    background-color: #56a998ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FieldError = styled.div`
  color: #d00;
  font-size: 11px;
  margin-bottom: 4px;
`;

const ErrorText = styled.div`
  color: #d00;
  margin-top: 4px;
  font-size: 12px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ListContainer = styled.div`
  margin: 0 0 20px;
  overflow-x: auto;
  border-right: 1px solid #dfe3e8;
  border-bottom: 1px solid #e6e6e6;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$showActions ? "1fr 1fr 220px 260px 160px" : "1fr 1fr 220px 260px")};
  align-items: center;
  padding: 0 20px;
  height: 50px;
  background: #f2f0f0;
  font-size: 18px;
  font-weight: 700;
  color: #6ab8a8;
  min-width: 900px;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 12px;
    border-right: 1px solid #d0d0d0;
    box-sizing: border-box;

    &:last-child {
      border-right: none;
    }
  }

  @media (max-width: 1024px) {
    grid-template-columns: ${(props) => (props.$showActions ? "0.8fr 0.8fr 190px 220px 140px" : "0.8fr 0.8fr 190px 220px")};
    font-size: 14px;
    padding: 0 16px;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 0 12px;
  }
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$showActions ? "1fr 1fr 220px 260px 160px" : "1fr 1fr 220px 260px")};
  align-items: center;
  padding: 0 20px;
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  color: #555;
  border-top: 1px solid #e6e6e6;
  min-width: 900px;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 12px;
    border-right: 1px solid #d0d0d0;
    box-sizing: border-box;

    &:last-child {
      border-right: none;
    }
  }

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  &:hover {
    background: #f9fefa;
  }

  @media (max-width: 1024px) {
    grid-template-columns: ${(props) => (props.$showActions ? "0.8fr 0.8fr 190px 220px 140px" : "0.8fr 0.8fr 190px 220px")};
    font-size: 15px;
    padding: 0 16px;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 0 12px;
    height: 44px;
  }
`;

const Cell = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  height: 100%;

  img {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 768px) {
    img {
      width: 18px;
      height: 18px;
    }
  }
`;

const HeaderLabel = styled.div`
  font-size: 11px;
  color: #ff9f1f;
`;

const HeaderActionsContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Actions = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const VoltarButton = styled.button`
  background-color: #999;
  color: #ffffff;
  border: none;
  padding: 10px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 9px;
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const EmptyMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: #555;
  font-size: 15px;
`;

const RestrictedMessage = styled.div`
  max-width: 700px;
  margin: 20px 40px;
  padding: 16px 24px;
  background-color: #fff8e6;
  border: 1px solid #f0d989;
  border-radius: 11px;
  color: #8a6d1a;
  font-size: 14px;
`;

const normalizarNomeArea = (nome = "") => nome
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .trim()
  .replace(/\s+/g, " ")
  .toLocaleLowerCase("pt-BR");

const AreasCurso = () => {
  const popup = usePopup();
  const [user, setUser] = useState(null);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ nome: "", descricao: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        setSidebarItems(getCargoIdSidebar(userData.cargoId));
      } catch (err) {
        console.error("Erro ao buscar usuário:", err);
      }
    };

    fetchUser();
  }, []);

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const result = await apiService.getAreaCursos();
      setAreas(result?.data ?? result ?? []);
    } catch (err) {
      await popup.alert({
        icon: "warning",
        message: err.message || "Falha ao carregar áreas de curso.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: name === "nome" ? validarNome(value) : "" }));
    }
  };

  const validarNome = (nome) => {
    const requiredError = required(nome);
    if (requiredError) return requiredError;

    const nomeComparavel = normalizarNomeArea(nome);
    const nomeDuplicado = areas.some(
      (area) => area.id !== editingId && normalizarNomeArea(area.nome) === nomeComparavel
    );

    return nomeDuplicado
      ? "Já existe uma área de curso cadastrada com este nome."
      : "";
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (name === "nome") {
      setFieldErrors((prev) => ({ ...prev, nome: validarNome(value) }));
    }
  };

  const getError = (name) => (touched[name] ? fieldErrors[name] : "");

  const resetForm = () => {
    setForm({ nome: "", descricao: "" });
    setTouched({});
    setFieldErrors({});
    setSubmitError(null);
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!submitting) resetForm();
  };

  const handleEdit = (area) => {
    setEditingId(area.id);
    setForm({ nome: area.nome, descricao: area.descricao || "" });
    setTouched({});
    setFieldErrors({});
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (area) => {
    const confirmed = await popup.confirm({
      icon: "delete",
      message: `Confirma exclusão da área "${area.nome}"?`,
    });
    if (!confirmed) return;

    setDeletingId(area.id);
    try {
      await apiService.deleteAreaCurso(area.id);
      setAreas((prev) => prev.filter((a) => a.id !== area.id));
      if (editingId === area.id) resetForm();
    } catch (err) {
      await popup.alert({
        icon: "warning",
        message: err.message || "Falha ao excluir área de curso.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const nomeError = validarNome(form.nome);
    setTouched((prev) => ({ ...prev, nome: true }));
    setFieldErrors((prev) => ({ ...prev, nome: nomeError }));
    if (nomeError) return;

    const payload = {
      nome: form.nome.trim(),
      descricao: form.descricao.trim() || undefined,
    };
    const isEditing = Boolean(editingId);

    setSubmitting(true);
    try {
      if (editingId) {
        const areaAtualizada = await apiService.updateAreaCurso(editingId, payload);
        setAreas((prev) =>
          sortByField(
            prev.map((a) =>
              a.id === editingId ? { ...a, ...areaAtualizada } : a
            ),
            "nome"
          )
        );
      } else {
        const novaArea = await apiService.createAreaCurso(payload);
        const novaAreaComContagem = {
          _count: { cursos: 0, vagas: 0 },
          ...novaArea,
        };
        setAreas((prev) => sortByField([...prev, novaAreaComContagem], "nome"));
      }
      resetForm();
      await popup.alert({
        icon: "success",
        message: isEditing
          ? `A área de curso "${payload.nome}" foi atualizada com sucesso.`
          : `A área de curso "${payload.nome}" foi cadastrada com sucesso.`,
      });
    } catch (err) {
      setSubmitError(
        err.message || `Erro ao ${editingId ? "atualizar" : "cadastrar"} área de curso.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Navbar
        onLogout={apiService.logout}
        showHome={true}
        showNotifications={true}
        showProfile={true}
        showLogout={true}
        user={user}
      />
      <ContentWrapper>
        <Sidebar items={sidebarItems} />
        <MainContent>
          <HeaderBar>
            <HeaderTitleGroup>
              <span>ÁREAS DE CURSO</span>
              {user?.cargoId === 1 && (
                <AddAreaButton type="button" onClick={handleAdd}>
                  Adicionar área
                  <img src={addCursoButton} alt="" />
                </AddAreaButton>
              )}
            </HeaderTitleGroup>
          </HeaderBar>
          <DescriptionBar>
            Cadastre e gerencie as áreas de conhecimento usadas para organizar cursos e ofertas de vagas.
          </DescriptionBar>

          <ListContainer>
            <HeaderRow $showActions={user?.cargoId === 1}>
              <div>Nome</div>
              <div>Descrição</div>
              <div>Cursos cadastrados</div>
              <div>Vagas cadastradas</div>
              {user?.cargoId === 1 && (
                <div>
                  <HeaderActionsContainer>
                    <HeaderAction>
                      <img src={editIcon} alt="Editar" />
                      <HeaderLabel>Editar</HeaderLabel>
                    </HeaderAction>
                    <HeaderAction>
                      <img src={trashIcon} alt="Deletar" />
                      <HeaderLabel>Deletar</HeaderLabel>
                    </HeaderAction>
                  </HeaderActionsContainer>
                </div>
              )}
            </HeaderRow>

            {loading && <EmptyMessage>Carregando...</EmptyMessage>}
            {!loading && areas.length === 0 && (
              <EmptyMessage>Nenhuma área de curso cadastrada.</EmptyMessage>
            )}
            {!loading &&
              areas.map((area) => {
                const emUso = (area._count?.cursos ?? 0) > 0 || (area._count?.vagas ?? 0) > 0;
                return (
                  <ListRow key={area.id} $showActions={user?.cargoId === 1}>
                    <Cell title={area.nome}>{area.nome}</Cell>
                    <Cell title={area.descricao || ""}>{area.descricao || "—"}</Cell>
                    <Cell>{area._count?.cursos ?? "—"}</Cell>
                    <Cell>{area._count?.vagas ?? "—"}</Cell>
                    {user?.cargoId === 1 && (
                      <Actions>
                        <IconButton
                          type="button"
                          onClick={() => handleEdit(area)}
                          title="Editar área"
                        >
                          <img src={editIcon} alt="Editar" style={{ width: 22, height: 22 }} />
                        </IconButton>
                        <IconButton
                          type="button"
                          onClick={() => handleDelete(area)}
                          disabled={emUso || deletingId === area.id}
                          title={emUso ? "Não é possível excluir: há cursos ou vagas vinculados" : "Excluir área"}
                        >
                          <img src={trashIcon} alt="Excluir" style={{ width: 22, height: 22 }} />
                        </IconButton>
                      </Actions>
                    )}
                  </ListRow>
                );
              })}
          </ListContainer>

          {user && user.cargoId !== 1 && (
            <RestrictedMessage>
              Somente administradores podem cadastrar novas áreas de curso.
            </RestrictedMessage>
          )}

          {isModalOpen && user?.cargoId === 1 && (
            <ModalOverlay role="presentation" onClick={handleCloseModal}>
              <ModalContent
                role="dialog"
                aria-modal="true"
                aria-labelledby="area-curso-modal-title"
                onClick={(event) => event.stopPropagation()}
              >
                <ModalHeader>
                  <span id="area-curso-modal-title">
                    {editingId ? "Editar área de curso" : "Adicionar área de curso"}
                  </span>
                  <ModalCloseButton
                    type="button"
                    onClick={handleCloseModal}
                    disabled={submitting}
                    aria-label="Fechar"
                  >
                    ×
                  </ModalCloseButton>
                </ModalHeader>
                <FormContainer>
                  <FormStyled onSubmit={handleSubmit}>
                    <Row>
                      <div style={{ flex: 1 }}>
                        <Label bold>Nome*</Label>
                        <Input
                          type="text"
                          name="nome"
                          value={form.nome}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Ex: Nutrição"
                          hasError={!!getError("nome")}
                          autoFocus
                        />
                        {getError("nome") && <FieldError>{getError("nome")}</FieldError>}
                      </div>
                    </Row>

                    <div>
                      <Label>Descrição</Label>
                      <Input
                        type="text"
                        name="descricao"
                        value={form.descricao}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Opcional"
                      />
                    </div>

                    {submitError && <ErrorText>{submitError}</ErrorText>}

                    <ActionsRow style={{ justifyContent: "space-between" }}>
                      <VoltarButton type="button" onClick={handleCloseModal} disabled={submitting}>
                        Cancelar
                      </VoltarButton>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? "Salvando..." : editingId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    </ActionsRow>
                  </FormStyled>
                </FormContainer>
              </ModalContent>
            </ModalOverlay>
          )}
        </MainContent>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default AreasCurso;
